import { Component, OnInit, OnDestroy, effect } from '@angular/core';
import { NgApexchartsModule } from "ng-apexcharts";
import { ReportService } from '../services/report.service';
import { ThemeService } from '../services/theme.service';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-sales-production-chart',
  standalone: true,
  imports: [
    NgApexchartsModule,
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatInputModule,
    MatNativeDateModule,
    TranslocoModule
  ],
  templateUrl: './sales-production-chart.component.html',
  styleUrl: './sales-production-chart.component.css'
})
export class SalesProductionChartComponent implements OnInit, OnDestroy {

  loadingSalesWeeklyComparison: boolean = false;
  chartOptionsSalesWeeklyComparison: any;
  chartOptionsProduction: any;
  chartOptionsWeek: any;
  chartOptionsMonth: any;
  startDate!: string;
  maxDate!: string;
  minDate!: string;
  private langSub?: Subscription;

  constructor(
    private reportService: ReportService,
    private transloco: TranslocoService,
    private themeService: ThemeService
  ) {
    // Recompõe o gráfico ao alternar o tema (legibilidade no claro/escuro)
    effect(() => {
      const mode = this.themeService.resolvedTheme();
      if (this.chartOptionsSalesWeeklyComparison) {
        // Atualiza o tema do gráfico já renderizado sem refazer a busca de dados
        this.chartOptionsSalesWeeklyComparison = {
          ...this.chartOptionsSalesWeeklyComparison,
          theme: { mode },
        };
      }
    });
  }

  /** Tema do ApexCharts derivado do tema resolvido (claro/escuro). */
  private chartTheme(): { mode: 'light' | 'dark' } {
    return { mode: this.themeService.resolvedTheme() };
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  ngOnInit() {
    const today = new Date();
    this.startDate = today.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }).split('/').reverse().join('-');
    this.maxDate = today.toISOString().split('T')[0];

    const pastDate = new Date();
    pastDate.setDate(today.getDate() - 90);
    this.minDate = pastDate.toISOString().split('T')[0];

    // Gráfico de Vendas (barras)
    this.GenerateEmptyWeeklyComparisionChart();

    this.ConfigureSalesWeeklyComparisonChart();
    // Recompõe ao trocar o idioma (nomes das séries/título)
    this.langSub = this.transloco.langChanges$.subscribe(() => this.ConfigureSalesWeeklyComparisonChart());
    // Gráfico de Produção (barras)

    const currentWeekDates = [
      '31/10/2024', '01/11/2024', '02/11/2024', '03/11/2024', '04/11/2024', '05/11/2024', '06/11/2024'
    ];
    const previousWeekDates = [
      '24/10/2024', '25/10/2024', '26/10/2024', '27/10/2024', '28/10/2024', '29/10/2024', '30/10/2024'
    ];

    this.chartOptionsProduction = {
      series: [
        {
          name: 'Esta Semana',
          data: [1200, 1500, 2000, 1800, 1700, 1600, 2200]
        },
        {
          name: 'Semana Passada',
          data: [1100, 1400, 1900, 1700, 1600, 1500, 2100]
        }
      ],
      chart: {
        type: 'bar',
        height: 350
      },
      xaxis: {
        categories: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']
      },
      dataLabels: {
        enabled: false
      },
      title: {
        text: 'Comparação de Produção',
        align: 'center'
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
          const label = w.globals.labels[dataPointIndex];
          let html = `
            <div style="border-radius: 5px; overflow: hidden; font-family: Arial, sans-serif; pointer-events: none; background: var(--surface-raised); color: var(--text-primary); border: 1px solid var(--border); box-shadow: 0 6px 18px rgba(0,0,0,0.25);">
              <div style="background-color: var(--surface-muted); padding: 5px; font-weight: bold; text-align: center; border-bottom: 1px solid var(--border);">
                ${label}
              </div>
              <div style="padding: 10px; display: flex; flex-direction: column; gap: 8px;">`;
          
          series.forEach((s: any, idx: number) => {
            const value = s[dataPointIndex];
            const color = w.globals.colors[idx];
            const name = w.globals.seriesNames[idx];
            const date = idx === 0 ? currentWeekDates[dataPointIndex] : previousWeekDates[dataPointIndex];
            
            html += `
              <div>
                <span style="display: inline-block; width: 10px; height: 10px; background-color: ${color}; border-radius: 50%; margin-right: 5px;"></span>
                <strong>${name}:</strong> ${value}<br/>
                <span style="font-size: 11px; color: var(--text-muted); margin-left: 15px;">Data: ${date}</span>
              </div>`;
          });
          
          html += `</div></div>`;
          return html;
        }
      }

    };

    // Gráfico para os produtos mais vendidos na semana
    this.chartOptionsWeek = {
      series: [{
        data: [350, 300, 280, 250, 200, 180, 160, 150, 130, 120]  // Quantidades de vendas da semana
      }],
      chart: {
        type: 'bar',
        height: 350,
        horizontal: true  // Gráfico de barras horizontais
      },
      xaxis: {
        categories: ['Camisa Polo', 'Calça Jeans', 'Jaqueta', 'Vestido', 'Saia', 'Short', 'Camisa Social', 'Blusa', 'Tênis', 'Sandália']
      },
      title: {
        text: 'Top 10 Produtos Mais Vendidos na Última Semana',
        align: 'center'
      }
    };

    // Gráfico para os produtos mais vendidos no mês
    this.chartOptionsMonth = {
      series: [{
        data: [1200, 1150, 1100, 1000, 950, 900, 850, 800, 750, 700]  // Quantidades de vendas do mês
      }],
      chart: {
        type: 'bar',
        height: 350,
        horizontal: true  // Gráfico de barras horizontais
      },
      xaxis: {
        categories: ['Calça Jeans', 'Camisa Polo', 'Vestido', 'Jaqueta', 'Blusa', 'Tênis', 'Saia', 'Short', 'Camisa Social', 'Sandália']
      },
      title: {
        text: 'Top 10 Produtos Mais Vendidos no Mês',
        align: 'center'
      }
    };
  }

  ConfigureSalesWeeklyComparisonChart(): void {

    const today = new Date();
    const brazilTimezone = 'America/Sao_Paulo';
    const todayInBrazil = new Date(today.toLocaleString("en-US", { timeZone: brazilTimezone }));
    const startDateUTC = new Date(this.startDate + 'T00:00:00Z');
  
    todayInBrazil.setHours(0, 0, 0, 0);
    startDateUTC.setUTCHours(0, 0, 0, 0);
  
    if (startDateUTC > todayInBrazil) {
      this.startDate = todayInBrazil.toISOString().split('T')[0];
    }

    this.loadingSalesWeeklyComparison = true;
    this.reportService.getWeeklyComparision(this.startDate).subscribe(
      (response) => {
        let data = response.body;

        if (!data) {
          this.loadingSalesWeeklyComparison = false;
          return;
        }

        const currentWeekDates = data.firstWeek.data.map(x => this.formatDate(x.date));
        const previousWeekDates = data.beforeWeek.data.map(x => this.formatDate(x.date));

        let firstSerie = {
          name: this.transloco.translate('charts.thisWeek'),
          data: data.firstWeek.data.map(x => x.total)
        };
        let secondSerie = {
          name: this.transloco.translate('charts.lastWeek'),
          data: data.beforeWeek.data.map(x => x.total)
        };

        const dateLabel = this.transloco.translate('charts.tooltipDate');
        this.chartOptionsSalesWeeklyComparison = {
          lastUpdate: data.lastUpdate,
          theme: this.chartTheme(),
          series: [secondSerie, firstSerie],
          chart: {
            type: 'bar',
            height: 400,
            width: '100%',
            toolbar: { show: false }
          },
          xaxis: {
            categories: data.daysNames,
            labels: { style: { fontSize: '12px' } }
          },
          dataLabels: {
            enabled: true,
            style: {
              colors: ['#ffffff', '#ffffff'], // Cor branca para ambas as séries
              fontSize: '13px',
              fontWeight: 'bold',
              cssClass: 'apexcharts-datalabel-shadow'
            },
            offsetY: -5,
            dropShadow: {
              enabled: true,
              color: '#000000',
              top: 2,
              left: 2,
              blur: 3,
              opacity: 0.8
            }
          },
          title: {
            text: this.transloco.translate('charts.salesComparison'),
            align: 'center',
            style: { fontSize: '14px' }
          },
          tooltip: {
            enabled: true,
            shared: true,
            intersect: false,
            followCursor: false,
            hideDelay: 100,
            fixed: {
              enabled: false
            },
            onDatasetHover: {
              highlightDataSeries: true
            },
            custom: function ({ series, seriesIndex, dataPointIndex, w }: { series: any; seriesIndex: any; dataPointIndex: any; w: any }) {
              const label = w.globals.labels[dataPointIndex];
              let html = `
                <div style="border-radius: 5px; overflow: hidden; font-family: Arial, sans-serif; pointer-events: none; background: var(--surface-raised); color: var(--text-primary); border: 1px solid var(--border); box-shadow: 0 6px 18px rgba(0,0,0,0.25);">
                  <div style="background-color: var(--surface-muted); padding: 5px; font-weight: bold; text-align: center; border-bottom: 1px solid var(--border);">
                    ${label}
                  </div>
                  <div style="padding: 10px; display: flex; flex-direction: column; gap: 8px;">`;
              
              series.forEach((s: any, idx: number) => {
                const value = s[dataPointIndex];
                const color = w.globals.colors[idx];
                const name = w.globals.seriesNames[idx];
                const date = idx === 1 ? currentWeekDates[dataPointIndex] : previousWeekDates[dataPointIndex];
                
                html += `
                  <div>
                    <span style="display: inline-block; width: 10px; height: 10px; background-color: ${color}; border-radius: 50%; margin-right: 5px;"></span>
                    <strong>${name}:</strong> ${value}<br/>
                    <span style="font-size: 11px; color: var(--text-muted); margin-left: 15px;">${dateLabel} ${date}</span>
                  </div>`;
              });
              
              html += `</div></div>`;
              return html;
            }
          },
          responsive: [
            {
              breakpoint: 768,
              options: {
                chart: {
                  height: 300,
                  width: '100%'
                },
                xaxis: {
                  labels: { style: { fontSize: '10px' } }
                },
                title: {
                  style: { fontSize: '12px' }
                },
                dataLabels: {
                  style: {
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }
                }
              }
            }
          ]
        };
        // Forçar redimensionamento
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 500);
        this.loadingSalesWeeklyComparison = false;
      },
      (error) => {
        this.loadingSalesWeeklyComparison = false;
      });
  }

  private GenerateEmptyWeeklyComparisionChart() {

    this.chartOptionsSalesWeeklyComparison = {
      theme: this.chartTheme(),
      series: [
        {
          name: this.transloco.translate('charts.thisWeek'),
          data: []
        },
        {
          name: this.transloco.translate('charts.lastWeek'),
          data: []
        }
      ],
      chart: {
        type: 'bar',
        height: '100%',
        width: '100%'
      },
      xaxis: {
        categories: []
      },
      dataLabels: {
        enabled: false
      },
      title: {
        text: this.transloco.translate('charts.salesComparison'),
        align: 'center'
      }
    };
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}`;
  }
}